/* eslint-disable react/no-multi-comp */
import React, { Suspense } from "react";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { SubscriptionButton } from "scmp-sdk/components/rosetta/subscription-button/styles";
import type {
  PlusMastheadTabEventEventParameters,
  PlusMastheadTabImpressionEvent,
} from "scmp-sdk/lib/google-analytics/ga4-types";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { usePlusHomePageUpdateIndicator } from "./hooks";
import { UpdateIndicator } from "./styles";

type Props = {
  className?: string;
  withImpressionTracking?: boolean;
  withUpdateIndicator?: boolean;
};
export const ScmpPlusButton: React.FunctionComponent<Props> = ({
  className,
  withImpressionTracking,
  withUpdateIndicator,
}) => (
  <>
    {withUpdateIndicator ? (
      <Suspense>
        <WithUpdateIndicator
          className={className}
          withImpressionTracking={withImpressionTracking}
        />
      </Suspense>
    ) : (
      <Base className={className} withImpressionTracking={withImpressionTracking} />
    )}
  </>
);

const Base: React.FunctionComponent<Props & { hasUnreadPlusHomePageUpdate?: boolean }> = ({
  className,
  hasUnreadPlusHomePageUpdate,
  withImpressionTracking,
  withUpdateIndicator,
}) => {
  const { handleTrackGA4 } = useSdkContext();

  const eventParameters: PlusMastheadTabEventEventParameters | undefined = withImpressionTracking
    ? {
        notification: hasUnreadPlusHomePageUpdate ? "active" : "inactive",
      }
    : undefined;

  const handleClick = () => {
    handleTrackGA4({
      action: "click",
      category: "plus",
      customized_parameters: eventParameters,
      subcategory: "masthead",
    });

    const url = new URL("/plus", location.origin);
    url.search = new URL(location.href).searchParams.toString();
    globalThis.location.href = url.toString();
  };

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<PlusMastheadTabImpressionEvent>({
      ga4TrackingHandler: handleTrackGA4,
      getGa4Event: () => ({
        action: "imp",
        category: "plus",
        customized_parameters: eventParameters,
        subcategory: "masthead",
      }),
      options: {
        isSendGA4Tracking: withImpressionTracking,
        shouldSendOnce: true,
        shouldThrottle: true,
      },
    });

  return (
    <SubscriptionButton
      className={className}
      onClick={handleClick}
      ref={withImpressionTracking ? captureTrackImpressionEventTargetElement : null}
    >
      SCMP <i>Plus</i>
      {withUpdateIndicator && <UpdateIndicator />}
    </SubscriptionButton>
  );
};

const WithUpdateIndicator: React.FunctionComponent<Props> = props => {
  const { hasUnreadPlusHomePageUpdate } = usePlusHomePageUpdateIndicator();

  return (
    <Base
      {...props}
      hasUnreadPlusHomePageUpdate={hasUnreadPlusHomePageUpdate}
      withUpdateIndicator={hasUnreadPlusHomePageUpdate}
    />
  );
};

ScmpPlusButton.displayName = "ScmpPlusButton";
